import TopBanner from "@/components/TopBanner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LotusIcon from "@/components/icons/LotusIcon";
import FixedQuizCTA from "@/components/FixedQuizCTA";
import { Heart, Briefcase, Users, Calendar, Sparkles, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

const Index = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      <TopBanner />
      <Header />
      
      {/* Hero Welcome Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-primary via-teal-dark to-teal-dark mandala-bg relative overflow-hidden">
        <div className="absolute inset-0 mandala-bg opacity-30" />
        <div className="container relative z-10 text-center px-4">
          <div className="max-w-4xl mx-auto animate-fade-up">
            <LotusIcon size={60} className="mx-auto mb-6 text-accent" />
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              Welcome to Siwon Galaxy
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/85 max-w-3xl mx-auto mb-8 leading-relaxed">
              Where relationships heal, inner silence blooms, and life unfolds with purpose—guided by Vedantic wisdom and modern psychology.
            </p>
          </div>
        </div>
      </section>

      {/* SIWON Framework Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="card-spiritual text-center">
              <div className="lotus-divider mb-6">
                <Sparkles className="w-8 h-8 text-accent" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-6">
                Discover <span className="text-gradient-lime">SIWON</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                A transformative framework that blends <strong className="text-primary">Silence</strong> (inner stillness) 
                and <strong className="text-primary">Wonderment</strong> (spiritual awe) to awaken your highest potential. 
                Through the <strong className="text-accent">3 A's—Awareness, Alignment, Awakening</strong>—layered across 
                the five koshas, Siwon guides couples and professionals and students from discord to harmony, habit to wisdom, and fear to freedom.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SIWON Harmony Journey Section */}
      <section className="py-16 bg-background" data-testid="siwon-journey-section">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="card-spiritual">
              <div className="text-center mb-10">
                <div className="lotus-divider mb-6">
                  <Sparkles className="w-8 h-8 text-accent" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-3">
                  The <span className="text-gradient-lime">SIWON Harmony Journey</span>
                  <span className="align-super text-sm text-accent ml-1">™</span>
                </h2>
                <p className="text-muted-foreground text-base italic">
                  Four progressive stages · From Clarity to Grace
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    step: "01",
                    title: "Awareness",
                    subtitle: "Prepares the Mind by Seeing Reality Clearly",
                    body: "Recognizing unconscious patterns, ego, conditioning, expectations, and emotional habits with honesty and clarity.",
                    outcome: "Clarity",
                  },
                  {
                    step: "02",
                    title: "Alignment",
                    subtitle: "Purifies the Life by Bringing Thoughts, Words, and Actions into Coherence",
                    body: "Living in truth through conscious choices, disciplined habits, purposeful relationships, and selfless service.",
                    outcome: "Integrity",
                  },
                  {
                    step: "03",
                    title: "Awakening",
                    subtitle: "Illuminates the Heart by Deepening into Wisdom, Surrender, and Devotion",
                    body: "As the mind becomes clear and life becomes aligned, the heart gradually opens to higher wisdom, humility, and surrender.",
                    outcome: "Receptivity",
                  },
                  {
                    step: "04",
                    title: "Harmony",
                    subtitle: "Blossoms Through Divine Grace into a Life of Balance, Love, and Order",
                    body: "Harmony is not manufactured by human effort — it naturally blossoms when a purified heart becomes receptive to Divine Grace.",
                    outcome: "Grace",
                    highlight: true,
                  },
                ].map((stage) => (
                  <div
                    key={stage.step}
                    className={`relative rounded-xl p-6 border transition-all hover:shadow-md ${
                      stage.highlight
                        ? "bg-accent/8 border-accent/30"
                        : "bg-background border-border"
                    }`}
                    data-testid={`journey-stage-${stage.step}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm ${
                        stage.highlight
                          ? "bg-accent text-accent-foreground"
                          : "bg-primary/10 text-primary"
                      }`}>
                        {stage.step}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-serif text-xl md:text-2xl font-bold text-primary leading-tight">
                          {stage.title}
                        </h3>
                        <p className="text-xs md:text-sm text-secondary italic mt-1 leading-snug">
                          {stage.subtitle}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm md:text-base text-foreground/85 leading-relaxed mb-4">
                      {stage.body}
                    </p>
                    <div className="pt-3 border-t border-border/60 flex items-center gap-2">
                      <span className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                        Outcome
                      </span>
                      <span className="text-sm font-semibold text-accent">
                        → {stage.outcome}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-center text-muted-foreground italic mt-10 text-base md:text-lg">
                Clarity · Integrity · Receptivity · <span className="text-accent font-semibold">Grace</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Harmonize Section */}
      <section className="py-16 bg-sage-dark mandala-bg">
        <div className="container">
          <div className="text-center mb-12">
            <div className="lotus-divider">
              <LotusIcon size={32} />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
              Harmonize Relationships & Transform Life
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Couples Section */}
            <div className="card-spiritual">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-accent/20 rounded-2xl flex items-center justify-center">
                  <Heart className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-primary">Couples</h3>
              </div>
              
              {/* Before & After Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Before Box */}
                <div className="p-4 bg-destructive/5 rounded-xl border-l-4 border-destructive/30">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-destructive/70 mb-3">Before</h4>
                  <ul className="space-y-2">
                    {["Endless blame games and emotional distance", "Emotional starvation and quiet disconnection", "Repetitive fights over chores and in-laws", "Faded passion and dead-end love", "Mutual distrust after betrayals"].map((item, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-destructive/50 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* After Box */}
                <div className="p-4 bg-accent/10 rounded-xl border-l-4 border-accent">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-accent mb-3">After</h4>
                  <ul className="space-y-2">
                    {["Compassionate understanding and unbreakable trust", "Deep intimacy and soulful presence with each other", "Harmonious teamwork and shared ease in daily life", "Reignited spark and joy-filled connection", "Healed wounds and mutual wonder reborn"].map((item, idx) => (
                      <li key={idx} className="text-sm text-primary font-medium flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Working Professionals/Students Section */}
            <div className="card-spiritual">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-primary/20 rounded-2xl flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-primary">Working Professionals / Students</h3>
              </div>
              
              {/* Before & After Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Before Box */}
                <div className="p-4 bg-destructive/5 rounded-xl border-l-4 border-destructive/30">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-destructive/70 mb-3">Before</h4>
                  <ul className="space-y-2">
                    {["Chronic burnout and zero energy at day's end", "Self-doubt and imposter syndrome in daily tasks", "Work-life chaos and constant overwhelm", "Soul-crushing routines with no meaning", "Fear-paralyzed failure and avoiding risks"].map((item, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-destructive/50 mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* After Box */}
                <div className="p-4 bg-accent/10 rounded-xl border-l-4 border-accent">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-accent mb-3">After</h4>
                  <ul className="space-y-2">
                    {["Vibrant vitality and steady daily flow", "Unshakeable confidence and bold career leaps", "Balanced rhythms that reclaim health, joy, and bonds", "Purposeful wisdom that lights your true path", "Fearless action and a life brimming with fulfillment"].map((item, idx) => (
                      <li key={idx} className="text-sm text-primary font-medium flex items-start gap-2">
                        <span className="text-accent mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proven Path Section */}
      <section className="py-16 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <div className="lotus-divider">
              <LotusIcon size={32} />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
              Proven Path to Harmony & Transformation
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="card-spiritual mb-8">
              <h3 className="font-serif text-xl md:text-2xl font-semibold text-primary mb-4 text-center">
                One-on-one sessions, workshops, and webinars
              </h3>
              <p className="text-muted-foreground text-center leading-relaxed">
                Using the Interactive <strong className="text-primary">SIWON Life Methodology</strong>, with customised 
                assessments that track your growth across the five koshas—from Annamaya to Anandamaya.
              </p>
            </div>

            {/* Discovery Tools CTA */}
            <div className="py-12 px-8 bg-gradient-to-b from-primary via-teal-dark to-teal-dark rounded-2xl mandala-bg relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-primary-foreground mb-2 text-center">
                  Diagnostic Quiz & Assessment Tools
                </h3>
                <p className="text-primary-foreground/80 text-center mb-6">
                  Take Our Free Assessments
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="p-6 bg-primary-foreground/10 backdrop-blur-sm rounded-xl border border-primary-foreground/20 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <Heart className="w-6 h-6 text-accent" />
                      <h4 className="font-serif font-semibold text-primary-foreground">
                        Relationship Harmony Assessment Quiz
                      </h4>
                    </div>
                    <p className="text-sm text-primary-foreground/70 mb-4 flex-grow">
                      Map your 3A's—Awareness, Alignment, Awakening—kosha-by-kosha, and see where your relationship patterns are rooted.
                    </p>
                    <Link 
                      to="/discovery/relationship-quiz" 
                      className="btn-lime w-full text-center"
                      data-testid="relationship-quiz-btn"
                    >
                      Start Quiz
                    </Link>
                  </div>

                  <div className="p-6 bg-primary-foreground/10 backdrop-blur-sm rounded-xl border border-primary-foreground/20 flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      <Sparkles className="w-6 h-6 text-accent" />
                      <h4 className="font-serif font-semibold text-primary-foreground">
                        Life Transformation Diagnostic Quiz
                      </h4>
                    </div>
                    <p className="text-sm text-primary-foreground/70 mb-4 flex-grow">
                      Uncover your personal patterns of wonder and silence, and discover where your inner growth wants to bloom next.
                    </p>
                    <Link 
                      to="/discovery/life-quiz" 
                      className="btn-lime w-full text-center"
                      data-testid="life-quiz-btn"
                    >
                      Start Quiz
                    </Link>
                  </div>
                </div>

                {/* Your Quiz Map Section */}
                <div className="bg-primary-foreground/5 backdrop-blur-sm rounded-xl p-6 border border-primary-foreground/10">
                  <h4 className="font-serif font-semibold text-primary-foreground text-center mb-4">Your Quiz Map</h4>
                  <p className="text-sm text-primary-foreground/80 text-center mb-4">Your score will show you as:</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="text-center p-3 bg-primary-foreground/10 rounded-lg">
                      <p className="font-semibold text-accent text-sm">Rooted</p>
                      <p className="text-xs text-primary-foreground/70">Beginning basics with gentle support</p>
                    </div>
                    <div className="text-center p-3 bg-primary-foreground/10 rounded-lg">
                      <p className="font-semibold text-accent text-sm">Growing</p>
                      <p className="text-xs text-primary-foreground/70">Aligning habits and interrupting patterns</p>
                    </div>
                    <div className="text-center p-3 bg-primary-foreground/10 rounded-lg">
                      <p className="font-semibold text-accent text-sm">Deepening</p>
                      <p className="text-xs text-primary-foreground/70">Awakening inner silence and wisdom</p>
                    </div>
                    <div className="text-center p-3 bg-primary-foreground/10 rounded-lg">
                      <p className="font-semibold text-accent text-sm">Blooming</p>
                      <p className="text-xs text-primary-foreground/70">Sustaining integration and lived harmony</p>
                    </div>
                  </div>
                  <p className="text-xs text-center text-primary-foreground/70">
                    After your quiz, you'll receive a short map of your current pattern and a gentle invitation to explore deeper work with Siwon—through one-on-one sessions, workshops, or guided webinars.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Community Section */}
      <section className="py-16 bg-primary mandala-bg">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-6">
              <Users className="w-12 h-12 text-accent mx-auto" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4">
              Join the Siwon Galaxy
            </h2>
            <p className="text-xl text-accent font-medium mb-6">
              A COMMUNITY based JOURNEY
            </p>
            <p className="text-primary-foreground/85 text-lg mb-8 leading-relaxed">
              Book a Session or Workshop & Webinar Today – Experience Vedic Wisdom-inspired tools, 
              emotional intelligence, and Siwon Galaxy's holistic methodology for unshakable bonds 
              and purposeful living.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-10">
              <a 
                href="https://chat.whatsapp.com/Ie7aaYNHDiOGPyZEPvxhfW"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-lime px-8 py-4 text-lg inline-flex items-center gap-2"
                data-testid="join-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Join WhatsApp
              </a>
              <a 
                href="https://learn.siwongalaxy.com/web/checkout/696794e19fa315cfdf89597b"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-lime px-8 py-4 text-lg inline-flex items-center gap-2"
                data-testid="book-session-btn"
              >
                <Calendar className="w-5 h-5" />
                Book a Session
              </a>
              <a 
                href="https://learn.siwongalaxy.com/web/checkout/696794e19fa315cfdf89597b"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground px-8 py-4 rounded-full font-medium transition-colors text-lg border border-primary-foreground/20"
              >
                Explore Workshops
              </a>
            </div>

            <div className="pt-8 border-t border-primary-foreground/20">
              <p className="font-serif text-2xl md:text-3xl text-primary-foreground italic">
                Your Inner <span className="text-accent">SILENCE</span> unfolds <span className="text-accent">WONDERMENT</span> in Life.
              </p>
            </div>
          </div>
        </div>
      </section>

      <FixedQuizCTA />
      <Footer onStartQuiz={() => {}} />
    </div>
  );
};

export default Index;
